import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, GraduationCap, Target, Drama, Wand2,
  Lightbulb, ShieldCheck, Share2, User, LogOut, Menu, Infinity as InfinityIcon,
  Rocket, ScanText, Users, Utensils,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { useAppMode, MODES } from "@/context/AppModeContext";
import AccessibilityToolbar from "@/components/AccessibilityToolbar";
import ReadAloudBar from "@/components/ReadAloudBar";

const NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/ask", label: "Ask", icon: Sparkles },
  { to: "/read", label: "Read This", icon: ScanText },
  { to: "/nutrition", label: "Nutrition", icon: Utensils },
  { to: "/learn", label: "Learn", icon: GraduationCap },
  { to: "/skills", label: "Progress", icon: Target },
  { to: "/practice", label: "Practice", icon: Drama },
  { to: "/career", label: "Career Test-Drive", icon: Rocket },
  { to: "/tools", label: "Life Tools", icon: Wand2 },
  { to: "/ideas", label: "Idea Lab", icon: Lightbulb },
  { to: "/safety", label: "Safety", icon: ShieldCheck },
  { to: "/family", label: "Family & School", icon: Users },
  { to: "/multiverse", label: "Multiverse", icon: Share2 },
  { to: "/profile", label: "Profile", icon: User },
];

function NavItems({ onClick, big }) {
  return (
    <nav className="flex flex-col gap-1" data-testid="main-nav">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onClick}
          data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-md px-3 ${big ? "py-3.5 text-lg" : "py-2.5 text-[0.95rem]"} font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`
          }
        >
          <n.icon className={big ? "w-6 h-6" : "w-5 h-5"} />
          <span>{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const { mode, setMode, isSenior } = useAppMode();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const big = isSenior;

  const Brand = (
    <div className="flex items-center gap-2.5 px-3 py-4">
      <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-primary-foreground">
        <InfinityIcon className="w-5 h-5" />
      </div>
      <div className="leading-tight">
        <div className="font-head font-extrabold text-base">inizio</div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Multiverse</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Sidebar (desktop) */}
      <aside className={`hidden lg:flex fixed inset-y-0 left-0 ${big ? "w-72" : "w-60"} flex-col border-r border-border bg-card/40 z-30`}>
        {Brand}
        <div className="px-3 flex-1 overflow-y-auto scrollbar-thin"><NavItems big={big} /></div>
        <button onClick={logout} data-testid="logout-btn" className="m-3 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground">
          <LogOut className="w-5 h-5" /> Log out
        </button>
      </aside>

      <div className={`${big ? "lg:pl-72" : "lg:pl-60"}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-20 glass border-b border-border">
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden h-10 w-10 grid place-items-center rounded-md border border-border" data-testid="mobile-menu" aria-label="Menu"><Menu className="w-5 h-5" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                {Brand}
                <div className="px-3"><NavItems big={big} onClick={() => setOpen(false)} /></div>
              </SheetContent>
            </Sheet>

            <div className="lg:hidden flex items-center gap-2 font-head font-extrabold"><InfinityIcon className="w-5 h-5 text-primary" />inizio</div>

            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              <div className="hidden sm:block">
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="w-[150px] rounded-full" data-testid="mode-switcher"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MODES.map((m) => <SelectItem key={m.key} value={m.key} data-testid={`mode-opt-${m.key}`}>{m.label} mode</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button onClick={() => navigate("/profile")} className="flex items-center gap-2" data-testid="header-profile">
                <Avatar className="h-9 w-9 border border-border">
                  <AvatarFallback className="bg-accent text-accent-foreground font-bold">{(user?.name || "U")[0].toUpperCase()}</AvatarFallback>
                </Avatar>
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 max-w-6xl mx-auto">{children}</main>
      </div>

      <AccessibilityToolbar />
      <ReadAloudBar />
    </div>
  );
}
